import React, { useState, useEffect, useRef } from 'react';
import { 
  Navigation, 
  MapPin, 
  User, 
  Phone, 
  Mail, 
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const getStatusColor = (status) => {
  switch (status) {
    case 'AVAILABLE': return 'var(--success)';
    case 'BUSY': return '#ef4444';
    case 'ON_TRIP': return 'var(--primary)';
    case 'ONLINE': return '#06b6d4';
    case 'PAUSED': return 'var(--warning)';
    default: return 'var(--text-muted)';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'AVAILABLE': return 'Disponible';
    case 'BUSY': return 'Occupé';
    case 'ON_TRIP': return 'En course';
    case 'ONLINE': return 'En ligne';
    case 'PAUSED': return 'En pause';
    default: return 'Hors ligne';
  }
};

const createDriverIcon = (driver, isSelected) => {
  const color = getStatusColor(driver.status);
  const heading = driver.heading || 0;
  const pulseHtml = driver.status === 'AVAILABLE' ? `<div class="driver-pulse" style="border-color: ${color}"></div>` : '';
  const arrowHtml = `<div class="driver-arrow" style="border-bottom-color: ${color}; transform: rotate(${heading}deg);"></div>`;
  const dotHtml = `<div class="driver-dot" style="background-color: ${color}; border: 2px solid ${isSelected ? '#ffffff' : '#090d16'}"></div>`;

  return L.divIcon({
    className: 'custom-driver-icon',
    html: `<div style="position: relative; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;">
      ${pulseHtml}
      ${arrowHtml}
      ${dotHtml}
    </div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12]
  });
};

export default function DriversMap() {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersGroupRef = useRef(null);

  const fetchDrivers = async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/drivers/map', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Impossible de charger les positions des chauffeurs');
      }

      const data = await response.json();
      setDrivers(data);

      // Keep selected driver updated if it exists using functional state update
      setSelectedDriver(prev => {
        if (!prev) return null;
        return data.find(d => d.driverId === prev.driverId) || prev;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial fetch and auto-refresh interval of 10 minutes
  useEffect(() => {
    fetchDrivers();
    const interval = setInterval(() => fetchDrivers(false), 600000); // 10 minutes
    return () => {
      clearInterval(interval);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Sync Leaflet map with drivers and selection
  useEffect(() => {
    if (!mapRef.current) return;

    if (!mapInstanceRef.current) {
      // Centered around Paris
      const map = L.map(mapRef.current).setView([48.8566, 2.3522], 12);
      mapInstanceRef.current = map;

      // Dark-themed tiles
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        maxZoom: 19
      }).addTo(map);

      markersGroupRef.current = L.featureGroup().addTo(map);
    }

    const map = mapInstanceRef.current;
    if (markersGroupRef.current) {
      markersGroupRef.current.clearLayers();
    }

    drivers.forEach(driver => {
      const isSelected = selectedDriver?.driverId === driver.driverId;
      const marker = L.marker([driver.latitude, driver.longitude], {
        icon: createDriverIcon(driver, isSelected)
      });
      
      marker.on('click', () => {
        setSelectedDriver(driver);
      });

      marker.addTo(markersGroupRef.current);
    });

    // Fit bounds to drivers if we just loaded them and no driver is selected
    if (drivers.length > 0 && !selectedDriver) {
      try {
        const bounds = markersGroupRef.current.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
        }
      } catch (e) {
        console.error("Error fitting bounds", e);
      }
    }
  }, [drivers, selectedDriver]);

  // Center map on selected driver when selectedDriver changes
  useEffect(() => {
    if (selectedDriver && mapInstanceRef.current) {
      mapInstanceRef.current.setView([selectedDriver.latitude, selectedDriver.longitude], 15);
    }
  }, [selectedDriver]);

  if (loading) {
    return <div style={styles.loadingContainer}>Chargement de la carte...</div>;
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <AlertCircle size={32} color="var(--danger)" />
        <p style={{ marginTop: '12px' }}>{error}</p>
        <button onClick={() => fetchDrivers(true)} style={styles.retryBtn} className="btn-secondary">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        /* Dark theme Leaflet overrides */
        .leaflet-container {
          background: var(--bg-primary) !important;
          border-radius: var(--radius-sm);
          border: 1px solid var(--border-color);
          font-family: var(--font-body) !important;
          width: 100%;
          height: 100%;
        }
        .leaflet-bar {
          border: 1px solid var(--border-color) !important;
          box-shadow: var(--shadow-main) !important;
        }
        .leaflet-bar a {
          background-color: var(--bg-secondary) !important;
          color: var(--text-primary) !important;
          border-bottom: 1px solid var(--border-color) !important;
          transition: background-color 0.2s ease;
        }
        .leaflet-bar a:hover {
          background-color: var(--bg-tertiary) !important;
          color: var(--primary) !important;
        }

        /* Custom Driver Icon styles */
        .custom-driver-icon {
          background: transparent;
          border: none;
        }
        .driver-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.4);
          position: absolute;
          top: 6px;
          left: 6px;
          z-index: 2;
        }
        .driver-arrow {
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-bottom: 8px solid;
          position: absolute;
          top: -2px;
          left: 8px;
          transform-origin: 4px 14px;
          z-index: 1;
        }
        .driver-pulse {
          position: absolute;
          top: -4px;
          left: -4px;
          width: 32px;
          height: 32px;
          border: 2px solid;
          border-radius: 50%;
          animation: pulse-ring 2s cubic-bezier(0.215, 0.610, 0.355, 1) infinite;
          opacity: 0;
          z-index: 0;
        }
        @keyframes pulse-ring {
          0% {
            transform: scale(0.5);
            opacity: 0.8;
          }
          100% {
            transform: scale(1.3);
            opacity: 0;
          }
        }
      `}</style>

      <div style={styles.headerRow}>
        <span style={styles.subtitle}>
          Visualisation en temps réel des chauffeurs actifs ({drivers.length}) - Rafraîchissement toutes les 10 min
        </span>
        <button 
          onClick={() => fetchDrivers(true)} 
          style={styles.refreshBtn} 
          disabled={refreshing}
          className="btn-secondary"
        >
          <RefreshCw size={14} className={refreshing ? 'spin-anim' : ''} />
          <span>{refreshing ? 'Actualisation...' : 'Actualiser'}</span>
        </button>
      </div>

      <div style={styles.mapGrid}>
        {/* Real Leaflet Map Container */}
        <div style={styles.canvasContainer} className="glass-card">
          <div ref={mapRef} style={{ width: '100%', height: '100%', minHeight: '400px' }} />

          {/* Compass/Legend Panel inside map */}
          <div style={styles.legendOverlay}>
            <span style={styles.legendTitle}>Statuts</span>
            <div style={styles.legendList}>
              <div style={styles.legendRow}>
                <div style={{ ...styles.legendDot, backgroundColor: 'var(--success)' }} />
                <span>Disponible</span>
              </div>
              <div style={styles.legendRow}>
                <div style={{ ...styles.legendDot, backgroundColor: 'var(--primary)' }} />
                <span>En course</span>
              </div>
              <div style={styles.legendRow}>
                <div style={{ ...styles.legendDot, backgroundColor: '#ef4444' }} />
                <span>Occupé</span>
              </div>
              <div style={styles.legendRow}>
                <div style={{ ...styles.legendDot, backgroundColor: '#06b6d4' }} />
                <span>En ligne</span>
              </div>
            </div>
          </div>
        </div>

        {/* Selected Driver Profile Panel */}
        <div style={styles.detailsContainer}>
          {selectedDriver ? (
            <div style={styles.detailsCard} className="glass-card">
              <div style={styles.detailsHeader}>
                <div style={styles.avatarLarge}>
                  <User size={24} />
                </div>
                <div>
                  <h4 style={styles.driverName}>{selectedDriver.name}</h4>
                  <div style={styles.badgeRow}>
                    <span 
                      style={{ 
                        ...styles.statusBadge, 
                        borderColor: getStatusColor(selectedDriver.status),
                        color: getStatusColor(selectedDriver.status),
                        backgroundColor: `${getStatusColor(selectedDriver.status)}15`
                      }}
                    >
                      {getStatusText(selectedDriver.status)}
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.detailsBody}>
                <div style={styles.infoRow}>
                  <Navigation size={16} color="var(--text-secondary)" />
                  <span style={styles.infoLabel}>Direction :</span>
                  <span style={styles.infoVal}>{Math.round(selectedDriver.heading)}°</span>
                </div>
                <div style={styles.infoRow}>
                  <MapPin size={16} color="var(--text-secondary)" />
                  <span style={styles.infoLabel}>Position :</span>
                  <span style={styles.infoVal}>
                    {selectedDriver.latitude.toFixed(5)}, {selectedDriver.longitude.toFixed(5)}
                  </span>
                </div>

                <div style={styles.divider} />

                <div style={styles.quickContact}>
                  <span style={styles.contactTitle}>Coordonnées</span>
                  <div style={styles.contactRow}>
                    <Mail size={14} color="var(--text-muted)" />
                    <span>chauffeur@safe.com</span>
                  </div>
                  <div style={styles.contactRow}>
                    <Phone size={14} color="var(--text-muted)" />
                    <span>+33 6 12 34 56 78</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => setSelectedDriver(null)} 
                style={styles.closeDetailsBtn} 
                className="btn-secondary"
              >
                Fermer la fiche
              </button>
            </div>
          ) : (
            <div style={styles.emptyDetailsCard} className="glass-card">
              <MapPin size={32} color="var(--text-muted)" />
              <p style={{ marginTop: '12px' }}>Sélectionnez un chauffeur sur la carte pour afficher sa fiche en direct.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: '100%',
    animation: 'fadeIn 0.4s ease',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-title)',
    fontSize: '1.1rem',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '40px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
  },
  retryBtn: {
    marginTop: '16px',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'var(--text-secondary)',
  },
  refreshBtn: {
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.85rem',
  },
  mapGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    flex: 1,
    minHeight: 0,
  },
  canvasContainer: {
    flex: 3,
    minWidth: '350px',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'stretch',
  },
  legendOverlay: {
    position: 'absolute',
    bottom: '16px',
    left: '16px',
    backgroundColor: 'rgba(15, 23, 42, 0.85)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius-sm)',
    padding: '12px 16px',
    backdropFilter: 'blur(8px)',
    zIndex: 1000,
  },
  legendTitle: {
    fontFamily: 'var(--font-title)',
    fontSize: '0.75rem',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
    display: 'block',
  },
  legendList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  legendRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.8rem',
    color: 'var(--text-primary)',
  },
  legendDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  detailsContainer: {
    flex: 1,
    minWidth: '280px',
    display: 'flex',
  },
  detailsCard: {
    width: '100%',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  emptyDetailsCard: {
    width: '100%',
    padding: '40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: 'var(--text-secondary)',
    fontSize: '0.85rem',
  },
  detailsHeader: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
  },
  avatarLarge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: 'var(--bg-tertiary)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-color)',
  },
  driverName: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  badgeRow: {
    marginTop: '4px',
  },
  statusBadge: {
    fontSize: '0.65rem',
    padding: '2px 8px',
    borderRadius: '9999px',
    border: '1px solid transparent',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  detailsBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1,
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '0.85rem',
  },
  infoLabel: {
    color: 'var(--text-secondary)',
    fontWeight: '500',
  },
  infoVal: {
    color: 'var(--text-primary)',
    fontWeight: '600',
  },
  divider: {
    height: '1px',
    backgroundColor: 'var(--border-color)',
    margin: '8px 0',
  },
  quickContact: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  contactTitle: {
    fontSize: '0.75rem',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.8rem',
    color: 'var(--text-primary)',
  },
  closeDetailsBtn: {
    width: '100%',
    padding: '10px',
  }
};
